// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RegattaPaymentDiscountFields from '../../fields/RegattaPaymentDiscountFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
type $Props = $Translate;

type $State = {
  data: Array<$Model<'RegattaPaymentDiscount'>>;
  loading: boolean;
};

const pageId = adminPages.adminRegattaPaymentDiscountsPage;

class AdminRegattaPaymentDiscountsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'RegattaPaymentDiscount') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyAdminRegattaPaymentDiscount();

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    const newItem: $ModelUpsertParams<'RegattaPaymentDiscount'> = {
      Content: null,
      Name: '',
      Used: false,
      Value: 0,
    };

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          rightButtons={(
            <FormDialog
              fields={(
                <RegattaPaymentDiscountFields item={newItem} />
              )}
              id={'create-regatta-payment-discount-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'RegattaPaymentDiscount'>>({
                    ...newItem,
                    ...values,
                  });

                  await Services.Api.upsertAdminRegattaPaymentDiscount(params);
                },
              })}
              subTitle={t('New')}
              title={t('Regatta payment discount')}
            >
              <Button
                color={'info'}
                id={'create-regatta-payment-discount-button'}
                size={'sm'}
                title={t('Add new discount code')}
              >
                {t('Add new')}
              </Button>
            </FormDialog>
          )}
          title={t('Regatta payment discounts')}
        />
      </Card>
    );
  }

  renderButtons(dataItem: $Model<'RegattaPaymentDiscount'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaPaymentDiscountFields item={dataItem} />
          )}
          id={`update-regatta-payment-discount-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaPaymentDiscount'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminRegattaPaymentDiscount(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta payment discount')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-payment-discount-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-payment-discount-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminRegattaPaymentDiscount(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('RegattaPaymentDiscount')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-payment-discount-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{`${t('Value')} (${t('Cents')})`}</th>
            <th className={'text-center'}>{t('Used')}</th>
            <th>{t('Description')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaPaymentDiscount'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Name}</td>
              <td>{dataItem.Value}</td>
              <td className={'text-center'}>
                {dataItem.Used === true && (
                  <em className={'icon-check'} />
                )}
              </td>
              <td>{dataItem.Content}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          )).reverse()}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminRegattaPaymentDiscountsPageView;
